class SinaMagneticaMiniCrmForm {
  constructor({ form }) {
    this.form = form;
    this.endpoint = form.action;
    this.formHash = form.getAttribute("formhash") || "";
  }

  buildFormData() {
    const data = new FormData(this.form);

    data.append("Dummy[][GDPR_Contribution][]", "1");
    data.append("Referrer", window.location.href);

    if (this.formHash) {
      data.append("FormHash", this.formHash);
    }

    return data;
  }

  async submit() {
    await fetch(this.endpoint, {
      method: "POST",
      mode: "no-cors",
      body: this.buildFormData()
    });
  }
}

class SinaMagneticaPersonalizare {
  constructor(root) {
    this.root = root;
    this.modal = root.querySelector("[data-sina-modal]");
    this.openButton = root.querySelector("[data-sina-modal-open]");
    this.closeButtons = root.querySelectorAll("[data-sina-modal-close]");
    this.form = root.querySelector("[data-sina-form]");
    this.status = root.querySelector("[data-sina-status]");
    this.success = root.querySelector("[data-sina-success]");
    this.submitButton = this.form?.querySelector(".banda-form__submit");
    this.lastFocusedElement = null;
    this.closeTimer = null;
    this.wasSubmitted = false;
    this.miniCrmForm = this.form ? new SinaMagneticaMiniCrmForm({ form: this.form }) : null;
  }

  init() {
    if (!this.modal || !this.openButton) {
      return;
    }

    this.bindModalEvents();
    this.bindFormEvents();
    this.bindDescriptionSelects();
  }

  bindModalEvents() {
    this.openButton.addEventListener("click", () => this.openModal());

    this.closeButtons.forEach((button) => {
      button.addEventListener("click", () => this.closeModal());
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !this.modal.hidden) {
        this.closeModal();
      }
    });
  }

  bindFormEvents() {
    this.form?.addEventListener("submit", async (event) => {
      event.preventDefault();
      this.wasSubmitted = true;
      this.clearStatus();

      if (!this.validateForm()) {
        this.form.querySelector(".banda-form__field--invalid input, .banda-form__field--invalid select, .banda-form__field--invalid textarea")?.focus();
        return;
      }

      this.setLoading(true);

      try {
        await this.miniCrmForm?.submit();
        this.form.reset();
        this.resetDescriptions();
        this.showSuccess();
      } catch (error) {
        console.error("MiniCRM submit failed:", error);
        this.showError("Trimiterea nu a reusit. Te rugam sa incerci din nou.");
        this.setLoading(false);
      }
    });

    this.form?.addEventListener("input", (event) => {
      if (!this.wasSubmitted || !(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) {
        return;
      }

      this.validateField(event.target);
    });

    this.form?.addEventListener("change", (event) => {
      if (event.target instanceof HTMLSelectElement) {
        this.updateDescription(event.target);
      }

      if (!this.wasSubmitted || !(event.target instanceof HTMLSelectElement)) {
        return;
      }

      this.validateField(event.target);
    });
  }

  bindDescriptionSelects() {
    this.root.querySelectorAll("[data-sina-described-select]").forEach((select) => {
      this.updateDescription(select);
    });
  }

  openModal() {
    window.clearTimeout(this.closeTimer);
    this.lastFocusedElement = document.activeElement;
    this.modal.hidden = false;
    document.body.style.overflow = "hidden";

    window.requestAnimationFrame(() => {
      this.modal.classList.add("banda-modal--open");
      this.modal.querySelector(".banda-modal__close")?.focus();
    });
  }

  closeModal() {
    this.modal.classList.remove("banda-modal--open");
    document.body.style.overflow = "";

    this.closeTimer = window.setTimeout(() => {
      this.modal.hidden = true;
      this.lastFocusedElement?.focus?.();
    }, 280);
  }

  getFieldMessage(input) {
    const value = input.value.trim();

    if (input.required && !value) {
      return "Completeaza acest camp.";
    }

    if (input.type === "email" && value && !input.validity.valid) {
      return "Introdu o adresa de email valida.";
    }

    if (input.type === "tel" && value && value.replace(/\D/g, "").length < 10) {
      return "Introdu un numar de telefon valid.";
    }

    return "";
  }

  validateField(input) {
    const field = input.closest("[data-sina-field]");
    const error = field?.querySelector("[data-sina-error]");
    const message = this.getFieldMessage(input);

    field?.classList.toggle("banda-form__field--invalid", Boolean(message));
    input.setAttribute("aria-invalid", String(Boolean(message)));

    if (error) {
      error.textContent = message;
    }

    return !message;
  }

  validateForm() {
    if (!this.form) {
      return true;
    }

    const inputs = [...this.form.querySelectorAll("input, select, textarea")];
    let isValid = true;

    inputs.forEach((input) => {
      if (!this.validateField(input)) {
        isValid = false;
      }
    });

    return isValid;
  }

  updateDescription(select) {
    const field = select.closest("[data-sina-field]");
    const description = field?.querySelector("[data-sina-description]");
    const title = field?.querySelector("[data-sina-description-title]");
    const text = field?.querySelector("[data-sina-description-text]");
    const note = field?.querySelector("[data-sina-description-note]");
    const option = select.selectedOptions[0];

    if (!description || !option || !select.value) {
      if (description) {
        description.hidden = true;
      }
      return;
    }

    if (title) {
      title.textContent = option.dataset.title || option.textContent.trim();
    }

    if (text) {
      text.textContent = option.dataset.description || "";
    }

    if (note) {
      note.textContent = option.dataset.note || "";
    }

    description.hidden = false;
  }

  resetDescriptions() {
    this.root.querySelectorAll("[data-sina-description]").forEach((description) => {
      description.hidden = true;
    });
  }

  setLoading(isLoading) {
    if (!this.submitButton) {
      return;
    }

    this.submitButton.disabled = isLoading;
    this.submitButton.classList.toggle("banda-form__submit--loading", isLoading);
  }

  clearStatus() {
    if (!this.status) {
      return;
    }

    this.status.textContent = "";
    this.status.classList.remove("banda-form__status--error");
  }

  showError(message) {
    if (!this.status) {
      return;
    }

    this.status.textContent = message;
    this.status.classList.add("banda-form__status--error");
  }

  showSuccess() {
    this.form?.querySelector(".banda-form__panel")?.setAttribute("hidden", "");
    this.form?.querySelector(".banda-form__notice")?.setAttribute("hidden", "");
    this.submitButton?.setAttribute("hidden", "");
    this.clearStatus();

    if (this.success) {
      this.success.hidden = false;
    }
  }
}

(() => {
  document.querySelectorAll("[data-sina-personalizare]").forEach((root) => {
    new SinaMagneticaPersonalizare(root).init();
  });
})();
